import { Token } from '@uniswap/sdk-core';
import { FeeAmount, tickToPrice } from '@uniswap/v3-sdk';
import JSBI from 'jsbi';
import { useMemo } from 'react';
import { PoolKey, PoolState } from '@/onchain/pools';
import { assetByAddress } from '@/onchain/assets';
import { toJSBI } from '@/utils/bigInt';
import { usePaginatedTickQuery } from '@/onchain/hooks/usePaginatedTickQuery';
import computeSurroundingTicks, { TickProcessed } from './utils/computeSurroundingTicks';
import { Ticks } from './types';

const PRICE_FIXED_DIGITS = 8;

const getActiveTick = (
  tickCurrent: number | undefined,
  feeAmount: FeeAmount | undefined,
  tickSpacing: number | undefined,
) =>
  tickCurrent && feeAmount && tickSpacing
    ? Math.floor(tickCurrent / tickSpacing) * tickSpacing
    : undefined;

export type ChartEntry = {
  activeLiquidity: number;
  price0: number;
  price1: number;
  tick: number;
};

export function useLiquidityChartData({
  chainId,
  poolKey,
  poolState,
}: {
  chainId: number;
  poolKey: PoolKey;
  poolState: PoolState;
}) {
  // Fetch all ticks
  const {
    data: ticksData,
    error: ticksError,
    isLoading: isTicksLoading,
  } = usePaginatedTickQuery(poolKey, 0);
  const ticks: Ticks = ticksData?.v3Pool?.ticks as Ticks;

  // Get pool data
  const pool = poolState;
  const liquidity = pool?.liquidity;
  const sqrtPriceX96 = pool?.sqrtPriceX96;
  const currentTick = pool?.tick;

  // Find nearest valid tick for pool in case tick is not initialized
  const activeTick = useMemo(
    () => getActiveTick(currentTick, poolKey.fee, poolKey.tickSpacing),
    [currentTick, poolKey.fee, poolKey.tickSpacing],
  );

  // Process active liquidity data
  const activeLiquidityData = useMemo(() => {
    if (
      activeTick === undefined ||
      !pool ||
      !ticks ||
      ticks.length === 0 ||
      isTicksLoading ||
      !chainId ||
      !poolKey.currency0 ||
      !poolKey.currency1
    ) {
      return {
        isLoading: isTicksLoading,
        error: ticksError,
        activeTick,
        data: undefined,
      };
    }

    const token0 = new Token(
      chainId,
      poolKey.currency0,
      assetByAddress(chainId, poolKey.currency0)?.exponent ?? 0,
    );
    const token1 = new Token(
      chainId,
      poolKey.currency1,
      assetByAddress(chainId, poolKey.currency1)?.exponent ?? 0,
    );

    // Find pivot for active tick
    const pivot = ticks.findIndex((tickData) => tickData?.tick && tickData.tick > activeTick) - 1;

    if (pivot < 0) {
      console.error('useLiquidityChartData: TickData pivot not found', {
        token0: token0.address,
        token1: token1.address,
        chainId: token0.chainId,
      });
      return {
        isLoading: isTicksLoading,
        error: ticksError,
        activeTick,
        data: undefined,
      };
    }

    let sdkPrice;
    try {
      sdkPrice = tickToPrice(token0, token1, activeTick);
    } catch (e) {
      console.error('useLiquidityChartData: Error getting price', {
        error: e,
        token0: token0.address,
        token1: token1.address,
        chainId: token0.chainId,
      });

      return {
        isLoading: isTicksLoading,
        error: e,
        activeTick,
        data: undefined,
      };
    }

    const activeTickProcessed: TickProcessed = {
      liquidityActive: toJSBI(pool?.liquidity ?? 0n),
      tick: activeTick,
      liquidityNet:
        Number(ticks[pivot]?.tick) === activeTick
          ? JSBI.BigInt(ticks[pivot]?.liquidityNet ?? 0)
          : JSBI.BigInt(0),
      price0: sdkPrice.toFixed(PRICE_FIXED_DIGITS),
      sdkPrice,
    };

    const subsequentTicks = computeSurroundingTicks(
      token0,
      token1,
      activeTickProcessed,
      ticks,
      pivot,
      true,
    );

    const previousTicks = computeSurroundingTicks(
      token0,
      token1,
      activeTickProcessed,
      ticks,
      pivot,
      false,
    );

    const ticksProcessed = previousTicks.concat(activeTickProcessed).concat(subsequentTicks);

    return {
      isLoading: isTicksLoading,
      error: ticksError,
      currentTick,
      activeTick,
      liquidity: toJSBI(liquidity ?? 0n),
      sqrtPriceX96: toJSBI(sqrtPriceX96 ?? 0n),
      data: ticksProcessed,
    };
  }, [
    activeTick,
    pool,
    ticks,
    isTicksLoading,
    chainId,
    poolKey.currency0,
    poolKey.currency1,
    ticksError,
    currentTick,
    liquidity,
    sqrtPriceX96,
  ]);

  // Format chart data
  const formattedData = useMemo(() => {
    if (!activeLiquidityData.data) return undefined;

    return activeLiquidityData.data.map((t) => ({
      activeLiquidity: parseFloat(t.liquidityActive.toString()),
      price0: parseFloat(t.sdkPrice.toSignificant(8)),
      price1: parseFloat(t.sdkPrice.invert().toSignificant(8)),
      tick: t.tick,
    }));
  }, [activeLiquidityData.data]);

  return {
    isLoading: activeLiquidityData.isLoading,
    error: activeLiquidityData.error,
    currentTick: activeLiquidityData.currentTick,
    activeTick: activeLiquidityData.activeTick,
    liquidity: activeLiquidityData.liquidity,
    sqrtPriceX96: activeLiquidityData.sqrtPriceX96,
    ticks,
    formattedData,
  };
}
